import {
  canAnimate,
  durationMs,
  motionToken,
  prefersReducedMotion,
} from "@/lib/motion";

type Placed = { el: HTMLElement; rect: DOMRect };
/// Keyed by list, then model: a pinned model has a row in Recent too.
export type RowSnapshot = Map<Element, Map<string, Placed>>;

const rowsOf = (list: Element) =>
  Array.from(list.querySelectorAll<HTMLElement>("[data-model-id]"));

export const snapshotRows = (root: Element): RowSnapshot =>
  new Map(
    Array.from(root.querySelectorAll("[data-overview-list]"), (list) => [
      list,
      new Map(
        rowsOf(list).map((el) => [
          el.dataset.modelId!,
          { el, rect: el.getBoundingClientRect() },
        ]),
      ),
    ]),
  );

/// Rows that stayed glide from where they were, a row that left fades where it
/// stood — as a copy in `host`, since React has already removed it — and a new
/// row fades in once the rows around it have mostly made room, so the two
/// never sit on top of each other.
export const glideRows = (before: RowSnapshot, host: HTMLElement) => {
  if (prefersReducedMotion() || !canAnimate(host)) return;
  const glide = {
    duration: durationMs("--duration-medium", 350),
    easing: motionToken("--ease-move", "ease-in-out"),
  };
  const short = durationMs("--duration-short", 200);
  for (const [list, was] of before) {
    const stayed = new Set<string>();
    for (const el of rowsOf(list)) {
      const id = el.dataset.modelId!;
      stayed.add(id);
      const from = was.get(id)?.rect;
      if (!from) {
        el.animate([{ opacity: 0 }, { opacity: 1 }], {
          duration: short,
          delay: short,
          easing: motionToken("--ease-enter", "ease-out"),
          fill: "backwards",
        });
        continue;
      }
      const to = el.getBoundingClientRect();
      const dx = from.left - to.left;
      const dy = from.top - to.top;
      if (dx || dy)
        el.animate(
          [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: "none" }],
          glide,
        );
    }
    for (const [id, { el, rect }] of was) {
      if (stayed.has(id)) continue;
      const ghost = el.cloneNode(true) as HTMLElement;
      ghost.removeAttribute("data-model-id");
      ghost.inert = true;
      const box = host.getBoundingClientRect();
      Object.assign(ghost.style, {
        position: "absolute",
        left: `${rect.left - box.left + host.scrollLeft}px`,
        top: `${rect.top - box.top + host.scrollTop}px`,
        width: `${rect.width}px`,
      });
      host.append(ghost);
      const remove = () => ghost.remove();
      void ghost
        .animate([{ opacity: 1 }, { opacity: 0 }], {
          duration: durationMs("--duration-micro", 100),
          easing: motionToken("--ease-exit", "ease-in"),
          fill: "forwards",
        })
        .finished.then(remove, remove);
    }
  }
};
